import { Modal, Descriptions, Avatar, Tag, Divider, Space, Button, message, Image, Spin, Select } from 'antd';
import { ModalProps } from 'antd';
import { CommunityPostResponseModel, RecruitmentStatus, UserType } from '@uniquegood/realworld-admin-interface';
import { UserOutlined, EyeOutlined, LikeOutlined, CommentOutlined, ExclamationCircleOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import React from 'react';
import { UserIdLink } from '@src/components/UserIdLink';
import { ProjectNameLink } from '@src/components/ProjectNameLink';
import { CopyableText } from '@src/components/CopyableText';
import { communityPostApi } from '@src/apis/admin';
import './PostDetailModal.css';

const userTypeToString: Record<UserType, string> = {
  [UserType.Normal]: '일반',
  [UserType.Creator]: '크리에이터',
  [UserType.Admin]: '관리자',
};

const userTypeToColor: Record<UserType, string> = {
  [UserType.Normal]: 'default',
  [UserType.Creator]: 'blue',
  [UserType.Admin]: 'red',
};

const recruitmentStatusToString: Record<RecruitmentStatus, string> = {
  [RecruitmentStatus.InProgress]: '모집중',
  [RecruitmentStatus.Completed]: '모집완료',
};

const recruitmentStatusToColor: Record<RecruitmentStatus, string> = {
  [RecruitmentStatus.InProgress]: 'green',
  [RecruitmentStatus.Completed]: 'default',
};

interface PostDetailModalProps {
  modalData: ModalProps;
  post: CommunityPostResponseModel | null;
  loading?: boolean;
  onDelete?: (postId: string) => Promise<void>;
  onEdit?: () => void;
  onRecruitmentStatusChange?: () => void;
}

export default function PostDetailModal({
  modalData,
  post,
  loading,
  onDelete,
  onEdit,
  onRecruitmentStatusChange,
}: PostDetailModalProps) {
  const [isUpdatingStatus, setIsUpdatingStatus] = React.useState(false);

  const handleDelete = () => {
    if (!post) return;
    Modal.confirm({
      title: '정말 삭제하시겠습니까?',
      icon: <ExclamationCircleOutlined />,
      okText: '예',
      cancelText: '아니요',
      okButtonProps: { danger: true },
      onOk: async () => {
        if (onDelete && post.id) {
          await onDelete(post.id);
        }
      },
    });
  };

  const handleRecruitmentStatusChange = async (status: RecruitmentStatus) => {
    if (!post?.id) return;

    try {
      setIsUpdatingStatus(true);
      const { data } = await communityPostApi.updateRecruitmentStatus(post.id, undefined, { status });
      if (data.success) {
        message.success('모집 상태가 변경되었습니다.');
        onRecruitmentStatusChange?.();
      }
    } catch (e) {
      console.error(e);
      message.error('모집 상태 변경에 실패했습니다.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const isDeleted = !!post?.deleteAt;

  const [isWriterIdHovered, setIsWriterIdHovered] = React.useState(false);

  const handleCopyWriterId = async () => {
    if (post?.writerId) {
      await navigator.clipboard.writeText(post.writerId);
      message.success('작성자 ID가 클립보드에 복사되었습니다.');
    }
  };

  return (
    <Modal
      {...modalData}
      title="게시물 상세"
      width={700}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onEdit} disabled={isDeleted || loading || !post}>
            수정
          </Button>
          <Button danger onClick={handleDelete} disabled={isDeleted || loading || !post}>
            삭제
          </Button>
        </div>
      }
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Spin size="large" />
        </div>
      ) : !post ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          게시글 정보를 불러올 수 없습니다.
        </div>
      ) : (
        <>
          <Descriptions column={2} bordered size="small" style={{ marginTop: 16 }}>
            <Descriptions.Item label="ID" span={2}>
              <span style={{ fontSize: 12, color: '#666' }}>{post.id}</span>
            </Descriptions.Item>
            <Descriptions.Item label="토픽">
              {post.topic?.name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="유저 타입">
              <Tag color={userTypeToColor[post.userType]}>{userTypeToString[post.userType]}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="작성자" span={2}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar
                  size="small"
                  src={post.writerProfileUrl}
                  icon={!post.writerProfileUrl && <UserOutlined />}
                />
                {post.writerName ? (
                  <>
                    <UserIdLink userId={post.writerId!}>{post.writerName}</UserIdLink>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={handleCopyWriterId}
                      onKeyDown={(e) => e.key === 'Enter' && handleCopyWriterId()}
                      onMouseEnter={() => setIsWriterIdHovered(true)}
                      onMouseLeave={() => setIsWriterIdHovered(false)}
                      style={{ fontSize: 12, color: '#999', cursor: 'pointer' }}
                    >
                      ({post.writerId})
                      <CopyOutlined
                        style={{
                          marginLeft: 4,
                          opacity: isWriterIdHovered ? 1 : 0,
                          transition: 'opacity 0.3s ease',
                        }}
                      />
                    </span>
                  </>
                ) : (
                  <span style={{ color: '#999' }}>(탈퇴한 사용자)</span>
                )}
              </div>
            </Descriptions.Item>
            {post.recruitmentStatus && (
              <Descriptions.Item label="모집 상태" span={2}>
                <Select
                  value={post.recruitmentStatus}
                  onChange={handleRecruitmentStatusChange}
                  loading={isUpdatingStatus}
                  disabled={isDeleted || isUpdatingStatus}
                  style={{ width: 120 }}
                  options={[
                    {
                      value: RecruitmentStatus.InProgress,
                      label: (
                        <Tag color={recruitmentStatusToColor[RecruitmentStatus.InProgress]} style={{ margin: 0 }}>
                          {recruitmentStatusToString[RecruitmentStatus.InProgress]}
                        </Tag>
                      ),
                    },
                    {
                      value: RecruitmentStatus.Completed,
                      label: (
                        <Tag color={recruitmentStatusToColor[RecruitmentStatus.Completed]} style={{ margin: 0 }}>
                          {recruitmentStatusToString[RecruitmentStatus.Completed]}
                        </Tag>
                      ),
                    },
                  ]}
                />
              </Descriptions.Item>
            )}
            <Descriptions.Item label="제목" span={2}>
              {post.title}
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left" style={{ marginTop: 24 }}>내용</Divider>
          <div
            style={{
              padding: 16,
              backgroundColor: '#fafafa',
              borderRadius: 8,
              minHeight: 100,
              maxHeight: 300,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {post.content || '(내용 없음)'}
          </div>

          {post.images && post.images.length > 0 && (
            <>
              <Divider orientation="left">첨부 이미지</Divider>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <Image.PreviewGroup
                  preview={{
                    classNames: {
                      wrapper: 'custom-image-preview',
                    },
                  }}
                >
                  {post.images.map((img) => (
                    <Image
                      key={img.id}
                      src={img.url || ''}
                      width={100}
                      height={100}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                    />
                  ))}
                </Image.PreviewGroup>
              </div>
            </>
          )}

          {post.linkUrls && post.linkUrls.length > 0 && (
            <>
              <Divider orientation="left">첨부 링크</Divider>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {post.linkUrls.map((url, index) => (
                  <a key={index} href={url} target="_blank" rel="noopener noreferrer">
                    {url}
                  </a>
                ))}
              </div>
            </>
          )}

          {post.taggedProject && (
            <>
              <Divider orientation="left">태그된 프로젝트</Divider>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {post.taggedProject.imageUrl && (
                  <img
                    src={post.taggedProject.imageUrl}
                    alt={post.taggedProject.name}
                    style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }}
                  />
                )}
                <ProjectNameLink projectName={post.taggedProject.name}>
                  {post.taggedProject.name}
                </ProjectNameLink>
                <CopyableText value={post.taggedProject.id} successMessage="프로젝트 ID가 클립보드에 복사되었습니다.">
                  ({post.taggedProject.id})
                </CopyableText>
              </div>
            </>
          )}

          {post.linkedProjects && post.linkedProjects.length > 0 && (
            <>
              <Divider orientation="left">링크된 프로젝트</Divider>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {post.linkedProjects.map((project) => (
                  <div key={project.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {project.imageUrl && (
                      <img
                        src={project.imageUrl}
                        alt={project.name}
                        style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }}
                      />
                    )}
                    <ProjectNameLink projectName={project.name}>
                      {project.name}
                    </ProjectNameLink>
                    <CopyableText value={project.id} successMessage="프로젝트 ID가 클립보드에 복사되었습니다.">
                      ({project.id})
                    </CopyableText>
                  </div>
                ))}
              </div>
            </>
          )}

          <Divider orientation="left">통계</Divider>
          <Space size="large">
            <span>
              <EyeOutlined style={{ marginRight: 4 }} />
              조회 {post.viewCount}
            </span>
            <span>
              <LikeOutlined style={{ marginRight: 4 }} />
              좋아요 {post.likeCount}
            </span>
            <span>
              <CommentOutlined style={{ marginRight: 4 }} />
              댓글 {post.commentCount}
            </span>
          </Space>

          <Divider orientation="left">일시</Divider>
          <Descriptions column={3} size="small">
            <Descriptions.Item label="작성일">
              {dayjs(post.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="수정일">
              {post.updatedAt ? dayjs(post.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="삭제일">
              {post.deleteAt ? (
                <span style={{ color: 'red' }}>{dayjs(post.deleteAt).format('YYYY-MM-DD HH:mm:ss')}</span>
              ) : '-'}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </Modal>
  );
}
